import React, { useContext, useEffect, useRef, useState } from "react";
import { Route, Routes, useSearchParams } from "react-router-dom";
import "./assets/scss/App.scss";
import getFile from "./components/helpers/getFile";
import { readEmbedParams } from "./components/helpers/embedProtocol";
import { isFakeRuntimeHealth } from "./components/helpers/runtimeHealth";
import { handleImageScale } from "./components/helpers/ImageHelper";
import {
  buildSam3PromptRequest,
  decodeMaskPng,
  deleteSam3Session,
  querySam3Prediction,
  setParmsandQueryEraseModel,
  setParmsandQueryModel,
} from "./components/helpers/modelAPI";
import { maskToImage, traceMaskToSVG } from "./components/helpers/mask_utils";
import { modelScaleProps, Sam3SessionDescriptor } from "./components/helpers/Interface";
import AppContext from "./components/hooks/createContext";
import i18n from "./i18n";
import Stage from "./components/Stage";

const App = () => {
  const {
    click: [, setClick],
    clicks: [clicks, setClicks],
    image: [image, setImage],
    prevImage: [prevImage, setPrevImage],
    svg: [, setSVG],
    svgs: [, setSVGs],
    allsvg: [, setAllsvg],
    isErased: [, setIsErased],
    isModelLoaded: [, setIsModelLoaded],
    isLoading: [, setIsLoading],
    segmentTypes: [, setSegmentTypes],
    maskImg: [, setMaskImg],
    isErasing: [, setIsErasing],
    stickerTabBool: [stickerTabBool],
    isMultiMaskMode: [isMultiMaskMode, setIsMultiMaskMode],
    isHovering: [, setIsHovering],
    showLoadingModal: [, setShowLoadingModal],
    eraserText: [, setEraserText],
    predMask: [predMask, setPredMask],
    predMasks: [predMasks, setPredMasks],
    predMasksHistory: [predMasksHistory, setPredMasksHistory],
    isToolBarUpload: [, setIsToolBarUpload],
    error: [, setError],
  } = useContext(AppContext)!;
  const [hasClicked, setHasClicked] = useState(false);
  const [mask, setMask] = useState<Uint8Array | null>(null);
  const [modelScale, setModelScale] = useState<modelScaleProps | null>(null);
  const [session, setSession] = useState<Sam3SessionDescriptor | null>(null);
  const [isFakeRuntime, setIsFakeRuntime] = useState(false);
  const sessionRef = useRef<Sam3SessionDescriptor | null>(null);
  const sessionRequestRef = useRef(0);
  const predictionRequestRef = useRef(0);
  const predMaskRef = useRef<string | null>(null);
  const predMasksHistoryRef = useRef<string[] | null>(null);
  const imageObjectUrlRef = useRef<string | null>(null);
  const [searchParams] = useSearchParams();

  predMaskRef.current = predMask;
  predMasksHistoryRef.current = predMasksHistory;

  useEffect(() => {
    const { lang } = readEmbedParams(searchParams.toString());
    if (lang && lang !== i18n.language) {
      i18n.changeLanguage(lang).catch((error) => console.error("Language change failed", error));
    }
  }, [searchParams]);

  useEffect(() => {
    const controller = new AbortController();
    fetch("/healthz", { signal: controller.signal })
      .then(async (response) => response.json())
      .then((health) => {
        setIsFakeRuntime(isFakeRuntimeHealth(health));
      })
      .catch((error) => {
        if (!controller.signal.aborted) console.error("Runtime check failed", error);
      });
    return () => controller.abort();
  }, []);

  const releaseSession = () => {
    const current = sessionRef.current;
    sessionRef.current = null;
    setSession(null);
    if (current) void deleteSam3Session(current.session_id);
  };

  useEffect(() => () => {
    const current = sessionRef.current;
    if (current) void deleteSam3Session(current.session_id);
    if (imageObjectUrlRef.current) URL.revokeObjectURL(imageObjectUrlRef.current);
  }, []);

  const handleSegModelResults = ({ session: nextSession }: { session: Sam3SessionDescriptor }) => {
    const previous = sessionRef.current;
    if (previous && previous.session_id !== nextSession.session_id) {
      void deleteSam3Session(previous.session_id);
    }
    sessionRef.current = nextSession;
    setSession(nextSession);
    setModelScale((previousScale) =>
      previousScale
        ? {
            ...previousScale,
            maskWidth: nextSession.width,
            maskHeight: nextSession.height,
          }
        : previousScale
    );
    setIsLoading(false);
    setIsErasing(false);
    setShowLoadingModal(false);
    setEraserText({ isErase: false, isEmbedding: false });
  };

  const handleAllModelResults = () => setAllsvg(null);

  const createSessionForImage = async (
    img: HTMLImageElement,
    width: number,
    height: number,
    uploadScale: number
  ) => {
    const generation = ++sessionRequestRef.current;
    try {
      await setParmsandQueryModel({
        width,
        height,
        uploadScale,
        imgData: img,
        handleSegModelResults: ({ session: nextSession }) => {
          if (generation === sessionRequestRef.current) {
            handleSegModelResults({ session: nextSession });
          } else {
            void deleteSam3Session(nextSession.session_id);
          }
        },
        handleAllModelResults,
        imgName: "",
        shouldDownload: false,
        shouldNotFetchAllModel: true,
      });
    } catch (error) {
      if (generation !== sessionRequestRef.current) return;
      console.error(error);
      setError(true);
      setIsLoading(false);
      setShowLoadingModal(false);
      window.alert(error instanceof Error ? error.message : "SAM3 session failed");
    }
  };

  useEffect(() => {
    if (
      !session ||
      !modelScale ||
      !clicks?.length ||
      !hasClicked ||
      stickerTabBool
    ) {
      return;
    }
    const controller = new AbortController();
    const generation = ++predictionRequestRef.current;
    setIsLoading(true);
    (async () => {
      try {
        const payload = buildSam3PromptRequest({
          sessionId: session.session_id,
          clicks,
          modelScale,
          previousPredictionId: predMaskRef.current,
          multimaskOutput: isMultiMaskMode,
        });
        const prediction = await querySam3Prediction(payload, controller.signal);
        const decodedMasks = await Promise.all(
          prediction.masks.map((item) =>
            decodeMaskPng(item.png_base64, prediction.width, prediction.height)
          )
        );
        if (generation !== predictionRequestRef.current) return;
        const bestIndex = Math.max(
          0,
          Math.min(prediction.best_index, decodedMasks.length - 1)
        );
        const tracedMasks = decodedMasks.map((item) =>
          traceMaskToSVG(item, prediction.width, prediction.height)
        );
        setSVG(tracedMasks[bestIndex]);
        setSVGs(isMultiMaskMode ? [...tracedMasks].reverse() : null);
        setMask(decodedMasks[bestIndex]);
        setMaskImg(maskToImage(decodedMasks[bestIndex], prediction.width, prediction.height));
        setPredMask(prediction.prediction_id);
        setClick(null);
        if (!predMasksHistoryRef.current) {
          setPredMasks([...(predMasks || []), prediction.prediction_id]);
        }
        setIsModelLoaded((previous) => ({ ...previous, boxModel: true }));
        setIsLoading(false);
      } catch (error) {
        if (controller.signal.aborted || generation !== predictionRequestRef.current) return;
        console.error(error);
        setError(true);
        setIsLoading(false);
        window.alert(error instanceof Error ? error.message : "SAM3 prediction failed");
      }
    })();
    return () => controller.abort();
  }, [clicks, hasClicked, isMultiMaskMode, modelScale, session, stickerTabBool]);

  const handleMagicErase = () => {
    if (!image || !mask) return;
    setIsErased(true);
    setIsErasing(true);
    setEraserText({ isErase: true, isEmbedding: false });
    const { height, width, uploadScale } = handleImageScale(image);
    setParmsandQueryEraseModel({
      width,
      height,
      uploadScale,
      imgData: image,
      mask,
      handlePredictedImage,
    });
  };

  const handlePredictedImage = (imgStr: string) => {
    const img = new Image();
    img.src = imgStr;
    img.onload = () => {
      setEraserText({ isErase: false, isEmbedding: true });
      img.width = image?.width || 0;
      img.height = image?.height || 0;
      handleImage(img);
    };
  };

  const handleImage = (img: HTMLImageElement = prevImage!) => {
    if (!img) return;
    releaseSession();
    setImage(img);
    setMaskImg(null);
    setSVG(null);
    setSVGs(null);
    setMask(null);
    setClicks(null);
    setIsModelLoaded({ boxModel: false, allModel: false });
    setHasClicked(false);
    setClick(null);
    setShowLoadingModal(true);
    const { height, width, uploadScale, scale } = handleImageScale(img);
    setModelScale({
      onnxScale: scale / uploadScale,
      maskWidth: Math.round(width * uploadScale),
      maskHeight: Math.round(height * uploadScale),
      scale,
      uploadScale,
      width,
      height,
    });
    void createSessionForImage(img, width, height, uploadScale);
  };

  const handleResetState = () => {
    sessionRequestRef.current += 1;
    predictionRequestRef.current += 1;
    releaseSession();
    if (imageObjectUrlRef.current) {
      URL.revokeObjectURL(imageObjectUrlRef.current);
      imageObjectUrlRef.current = null;
    }
    setMaskImg(null);
    setHasClicked(false);
    setClicks(null);
    setSVG(null);
    setSVGs(null);
    setAllsvg(null);
    setImage(null);
    setPrevImage(null);
    setPredMask(null);
    setPredMasks(null);
    setPredMasksHistory(null);
    setMask(null);
    setIsErased(false);
    setShowLoadingModal(false);
    setIsModelLoaded({ boxModel: false, allModel: false });
    setSegmentTypes("Click");
    setIsLoading(false);
    setIsMultiMaskMode(false);
    setIsHovering(null);
    setError(false);
  };

  const handleSelectedImage = async (
    data: File | URL,
    options?: { shouldNotFetchAllModel?: boolean; shouldDownload?: boolean }
  ) => {
    try {
      handleResetState();
      const selectionGeneration = sessionRequestRef.current;
      setShowLoadingModal(true);
      const imgData: File = data instanceof File ? data : await getFile(data as URL);
      if (selectionGeneration !== sessionRequestRef.current) return;
      const img = new Image();
      const objectUrl = URL.createObjectURL(imgData);
      imageObjectUrlRef.current = objectUrl;
      img.onload = () => {
        if (selectionGeneration !== sessionRequestRef.current) return;
        setIsToolBarUpload(false);
        const { height, width, scale, uploadScale } = handleImageScale(img);
        setModelScale({
          onnxScale: scale / uploadScale,
          maskWidth: Math.round(width * uploadScale),
          maskHeight: Math.round(height * uploadScale),
          scale,
          uploadScale,
          width,
          height,
        });
        img.width = Math.round(width * scale);
        img.height = Math.round(height * scale);
        setImage(img);
        setPrevImage(img);
        setIsErased(false);
        void createSessionForImage(img, width, height, uploadScale);
      };
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        if (imageObjectUrlRef.current === objectUrl) imageObjectUrlRef.current = null;
        setShowLoadingModal(false);
        setError(true);
        window.alert("Unable to load image");
      };
      img.src = objectUrl;
    } catch (error) {
      console.error(error);
      setShowLoadingModal(false);
      setError(true);
      window.alert(error instanceof Error ? error.message : "Unable to load image");
    }
  };

  useEffect(() => {
    const { file } = readEmbedParams(window.location.search);
    if (!file) return;
    (async () => {
      try {
        const response = await fetch(file);
        if (!response.ok) throw new Error(`Unable to fetch image (${response.status})`);
        const blob = await response.blob();
        await handleSelectedImage(new File([blob], "uploadfile", { type: blob.type }));
      } catch (error) {
        console.error(error);
        setError(true);
        window.alert(error instanceof Error ? error.message : "Unable to load image");
      }
    })();
  }, []);

  return (
    <Routes>
      <Route
        path="/"
        element={
          <div className="flex flex-col h-full overflow-hidden">
            {isFakeRuntime && (
              <div className="runtime-warning" role="status">
                {i18n.language.startsWith("zh")
                  ? "模拟分割运行中，不可作为 SAM3 模型验收"
                  : "Simulated segmentation is active and is not valid for SAM3 acceptance"}
              </div>
            )}
            <Stage
              scale={modelScale}
              handleResetState={handleResetState}
              handleMagicErase={handleMagicErase}
              handleImage={handleImage}
              hasClicked={hasClicked}
              setHasClicked={setHasClicked}
              handleSelectedImage={handleSelectedImage}
              image={image}
            />
          </div>
        }
      />
    </Routes>
  );
};

export default App;
